import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Card, Steps, Button, Space, Select, Input, message, Descriptions, Tag, Collapse, List, Tree } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, PauseCircleOutlined, PlayCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { testRunService } from '../services/testRunService';
import { userService } from '../services/userService';
import { testItemService } from '../services/testItemService';
import { TestRun, TestRunStatus, TestRunItem, TestRunItemStatus, TestRunStep, TestRunStepStatus } from '../types/testRun';
import { User } from '../types/user';
import { TestItem, TestItemType } from '../types/testItem';

const { Title } = Typography;
const { Step } = Steps;
const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

const TestRunExecutionPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [testRun, setTestRun] = useState<TestRun | null>(null);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [testItems, setTestItems] = useState<TestItem[]>([]);

    useEffect(() => {
        if (id) {
            fetchTestRun(parseInt(id));
            fetchUsers();
        }
    }, [id]);

    const fetchUsers = async () => {
        try {
            const data = await userService.getUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error(t('users.fetchError'));
        }
    };

    const fetchTestItems = async (projectId: number, itemIds: number[]) => {
        try {
            const data = await testItemService.getTestItems({
                project_id: projectId,
                ids: itemIds
            });
            setTestItems(data);
        } catch (error) {
            console.error('Error fetching test items:', error);
            message.error(t('testItems.errors.loadFailed'));
        }
    };

    const fetchTestRun = async (testRunId: number) => {
        try {
            setLoading(true);
            const data = await testRunService.getTestRun(testRunId);
            setTestRun(data);
            
            if (data.test_run_items && Array.isArray(data.test_run_items)) {
                const itemIds = data.test_run_items.map((item: TestRunItem) => item.test_item_id);
                await fetchTestItems(data.project_id, itemIds);
            }
        } catch (error) {
            console.error('Error fetching test run:', error);
            message.error(t('testRuns.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const getTestItemName = (itemId: number): string => {
        const item = testItems.find(i => i.id === itemId);
        return item?.name || '';
    };

    const handleComplete = async () => {
        if (!testRun) return;
        try {
            setLoading(true);
            await testRunService.completeTestRun(testRun.id);
            await fetchTestRun(testRun.id);
            message.success(t('testRuns.completeSuccess'));
        } catch (error) {
            message.error(t('testRuns.completeError'));
        } finally {
            setLoading(false);
        }
    };

    const handleAssignUser = async (itemId: number, userId: number) => {
        try {
            await testRunService.assignUserToItem(itemId, userId);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.assignSuccess'));
        } catch (error) {
            message.error(t('testRuns.assignError'));
        }
    };

    const handleStepStatusChange = async (stepId: number, status: TestRunStepStatus, actualResult?: string) => {
        try {
            console.log('Handling step status change:', { stepId, status, actualResult });
            console.log('Available statuses:', Object.values(TestRunStepStatus));
            console.log('Is status valid:', Object.values(TestRunStepStatus).includes(status));

            await testRunService.updateTestStepStatus(stepId, status, actualResult);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.stepStatusUpdateSuccess'));
        } catch (error: any) {
            console.error('Error updating test step status:', error);
            message.error(error.response?.data?.detail?.[0]?.msg || error.message || t('testRuns.stepStatusUpdateError'));
        }
    };

    const handleChecklistItemStatusChange = async (stepId: number, itemIndex: number, status: TestRunStepStatus) => {
        try {
            console.log('Handling checklist item status change:', { stepId, itemIndex, status });
            await testRunService.updateTestStepStatus(stepId, status);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.checklistItemStatusUpdateSuccess'));
        } catch (error: any) {
            console.error('Error updating checklist item status:', error);
            message.error(error.response?.data?.detail?.[0]?.msg || error.message || t('testRuns.checklistItemStatusUpdateError'));
        }
    };

    const handleChecklistItemResultChange = async (stepId: number, itemIndex: number, actualResult: string) => {
        try {
            await testRunService.updateChecklistItemResult(stepId, itemIndex, actualResult);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.checklistItemResultUpdateSuccess'));
        } catch (error) {
            message.error(t('testRuns.checklistItemResultUpdateError'));
        }
    };

    const handleItemStatusChange = async (itemId: number, status: TestRunItemStatus) => {
        try {
            console.log('Handling item status change:', { itemId, status });
            console.log('Status type:', typeof status);
            console.log('Status value:', status);
            console.log('Available statuses:', Object.values(TestRunItemStatus));
            console.log('Is status valid:', Object.values(TestRunItemStatus).includes(status));

            if (!Object.values(TestRunItemStatus).includes(status)) {
                throw new Error(`Invalid status: ${status}`);
            }

            await testRunService.updateTestItemStatus(itemId, status);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.statusUpdateSuccess'));
        } catch (error: any) {
            console.error('Error updating test item status:', error);
            message.error(error.response?.data?.detail?.[0]?.msg || error.message || t('testRuns.statusUpdateError'));
        }
    };

    const handleItemCommentChange = async (itemId: number, comment: string) => {
        try {
            await testRunService.updateTestItemComment(itemId, comment);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.commentUpdateSuccess'));
        } catch (error) {
            message.error(t('testRuns.commentUpdateError'));
        }
    };

    const getStatusColor = (status: TestRunItemStatus | TestRunStepStatus) => {
        switch (status) {
            case TestRunItemStatus.PASSED:
            case TestRunStepStatus.PASSED:
                return 'success';
            case TestRunItemStatus.FAILED:
            case TestRunStepStatus.FAILED:
                return 'error';
            case TestRunItemStatus.BLOCKED:
            case TestRunStepStatus.BLOCKED:
                return 'warning';
            case TestRunItemStatus.SKIPPED:
            case TestRunStepStatus.SKIPPED:
                return 'default';
            default:
                return 'processing';
        }
    };

    const getStatusIcon = (status: TestRunItemStatus | TestRunStepStatus) => {
        switch (status) {
            case TestRunItemStatus.PASSED:
            case TestRunStepStatus.PASSED:
                return <CheckCircleOutlined />;
            case TestRunItemStatus.FAILED:
            case TestRunStepStatus.FAILED:
                return <CloseCircleOutlined />;
            case TestRunItemStatus.BLOCKED:
            case TestRunStepStatus.BLOCKED:
                return <PauseCircleOutlined />;
            case TestRunItemStatus.IN_PROGRESS:
                return <PlayCircleOutlined />;
            default:
                return null;
        }
    };

    const renderTestItemContent = (item: TestRunItem) => {
        const testItem = testItems.find(ti => ti.id === item.test_item_id);
        if (!testItem) return null;

        return (
            <div>
                {/* Test Data Section */}
                {((item.folder?.test_data && item.folder.test_data.length > 0) || 
                  (item.test_item?.test_data && item.test_item.test_data.length > 0)) && (
                    <div style={{ marginBottom: '16px' }}>
                        <h4>Test Data</h4>
                        <List
                            dataSource={[...(item.folder?.test_data || []), ...(item.test_item?.test_data || [])]}
                            renderItem={(data) => (
                                <List.Item>
                                    <Card style={{ width: '100%' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <strong>{data.name}</strong>
                                        </div>
                                        {data.description && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Description:</strong> {data.description}
                                            </div>
                                        )}
                                        <div style={{ marginBottom: '8px' }}>
                                            <strong>Data:</strong>
                                            <pre style={{ marginTop: '8px', padding: '8px', background: '#f5f5f5' }}>
                                                {JSON.stringify(data.data, null, 2)}
                                            </pre>
                                        </div>
                                        {data.tags && data.tags.length > 0 && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Tags:</strong>{' '}
                                                {data.tags.map((tag, index) => (
                                                    <Tag key={index} style={{ marginLeft: '4px' }}>{tag}</Tag>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                </List.Item>
                            )}
                        />
                    </div>
                )}

                {/* Shared Steps Section */}
                {((item.folder?.shared_steps && item.folder.shared_steps.length > 0) || 
                  (item.test_item?.shared_steps && item.test_item.shared_steps.length > 0)) && (
                    <div style={{ marginBottom: '16px' }}>
                        <h4>Shared Steps</h4>
                        <List
                            dataSource={[...(item.folder?.shared_steps || []), ...(item.test_item?.shared_steps || [])]}
                            renderItem={(step) => (
                                <List.Item>
                                    <Card style={{ width: '100%' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <strong>{step.name}</strong>
                                        </div>
                                        {step.description && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Description:</strong> {step.description}
                                            </div>
                                        )}
                                        <div style={{ marginBottom: '8px' }}>
                                            <strong>Steps:</strong>
                                            <List
                                                dataSource={step.steps}
                                                renderItem={(subStep, index) => (
                                                    <List.Item>
                                                        <div style={{ marginBottom: '4px' }}>
                                                            <strong>Step {index + 1}:</strong> {subStep.action}
                                                        </div>
                                                        {subStep.expected_result && (
                                                            <div>
                                                                <strong>Expected Result:</strong> {subStep.expected_result}
                                                            </div>
                                                        )}
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    </Card>
                                </List.Item>
                            )}
                        />
                    </div>
                )}

                {/* Test Steps Section */}
                {testItem.type === TestItemType.TEST_CASE && testItem.steps && (
                    <div>
                        <h4>Test Steps</h4>
                        <List
                            dataSource={testItem.steps}
                            renderItem={(step, index) => {
                                const testRunStep = item.test_run_steps?.find(s => s.step_number === index + 1);
                                return (
                                    <List.Item>
                                        <Card style={{ width: '100%' }}>
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Step {index + 1}:</strong> {step.action}
                                            </div>
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Expected Result:</strong> {step.expected_result}
                                            </div>
                                            {step.test_data && (
                                                <div style={{ marginBottom: '8px' }}>
                                                    <strong>Test Data:</strong> {step.test_data}
                                                </div>
                                            )}
                                            <div style={{ marginTop: '16px' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <Select
                                                        value={testRunStep?.status || TestRunStepStatus.NOT_STARTED}
                                                        onChange={(value: TestRunStepStatus) => {
                                                            if (testRunStep) {
                                                                handleStepStatusChange(testRunStep.id, value);
                                                            } else {
                                                                handleCreateStep(item.id, index + 1, value);
                                                            }
                                                        }}
                                                        style={{ width: 200 }}
                                                    >
                                                        {Object.values(TestRunStepStatus).map(status => (
                                                            <Option key={status} value={status}>
                                                                {t(`testRuns.stepStatus.${status}`)}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                    <TextArea
                                                        value={testRunStep?.actual_result || ''}
                                                        onChange={(e) => {
                                                            if (testRunStep) {
                                                                handleStepStatusChange(
                                                                    testRunStep.id,
                                                                    testRunStep.status,
                                                                    e.target.value
                                                                );
                                                            }
                                                        }}
                                                        placeholder={t('testRuns.addActualResult')}
                                                        rows={4}
                                                    />
                                                </Space>
                                            </div>
                                        </Card>
                                    </List.Item>
                                );
                            }}
                        />
                    </div>
                )}

                {/* Checklist Section */}
                {testItem.type === TestItemType.CHECKLIST && testItem.check_items && (
                    <div>
                        <h4>Checklist Items</h4>
                        <List
                            dataSource={testItem.check_items}
                            renderItem={(checkItem, index) => {
                                const testRunStep = item.test_run_steps?.find(s => s.step_number === index + 1);
                                return (
                                    <List.Item>
                                        <Card style={{ width: '100%' }}>
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Check {index + 1}:</strong> {checkItem.description}
                                            </div>
                                            <div style={{ marginTop: '16px' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <Select
                                                        value={testRunStep?.status || TestRunStepStatus.NOT_STARTED}
                                                        onChange={(value: TestRunStepStatus) => {
                                                            if (testRunStep) {
                                                                handleChecklistItemStatusChange(testRunStep.id, index, value);
                                                            } else {
                                                                handleCreateStep(item.id, index + 1, value);
                                                            }
                                                        }}
                                                        style={{ width: 200 }}
                                                    >
                                                        {Object.values(TestRunStepStatus).map(status => (
                                                            <Option key={status} value={status}>
                                                                {t(`testRuns.stepStatus.${status}`)}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                    <TextArea
                                                        value={testRunStep?.actual_result || ''}
                                                        onChange={(e) => {
                                                            if (testRunStep) {
                                                                handleChecklistItemResultChange(testRunStep.id, index, e.target.value);
                                                            }
                                                        }}
                                                        placeholder={t('testRuns.addActualResult')}
                                                        rows={4}
                                                    />
                                                </Space>
                                            </div>
                                        </Card>
                                    </List.Item>
                                );
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    const handleCreateStep = async (itemId: number, stepNumber: number, status: TestRunStepStatus) => {
        try {
            console.log('Creating step with status:', status);
            console.log('Available statuses:', Object.values(TestRunStepStatus));
            console.log('Is status valid:', Object.values(TestRunStepStatus).includes(status));

            const stepData = {
                step_number: stepNumber,
                status: status,
                description: '',  // Required by the API
                expected_result: ''  // Required by the API
            };
            console.log('Sending step data:', stepData);

            await testRunService.createTestStep(itemId, stepData);
            await fetchTestRun(testRun!.id);
            message.success(t('testRuns.stepCreateSuccess'));
        } catch (error: any) {
            console.error('Error creating test step:', error);
            message.error(error.response?.data?.detail?.[0]?.msg || error.message || t('testRuns.stepCreateError'));
        }
    };

    const buildTreeData = (items: TestRunItem[]) => {
        const folderMap = new Map();
        const rootItems: any[] = [];

        items.forEach(item => {
            const folderId = item.folder_id;
            const folderName = item.folder?.name || 'Root';
            
            if (!folderMap.has(folderId)) {
                const folderNode = {
                    key: `folder-${folderId}`,
                    title: folderName,
                    children: [],
                    isFolder: true
                };
                folderMap.set(folderId, folderNode);
                rootItems.push(folderNode);
            }

            const folderNode = folderMap.get(folderId);
            const itemNode = {
                key: `item-${item.id}`,
                title: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{item.test_item?.name}</span>
                        <Space>
                            <Select
                                value={item.status}
                                onChange={(value) => handleItemStatusChange(item.id, value as TestRunItemStatus)}
                                style={{ width: 200 }}
                            >
                                {Object.values(TestRunItemStatus).map(status => (
                                    <Option key={status} value={status}>
                                        {t(`testRuns.itemStatus.${status}`)}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                value={item.assigned_to_id}
                                onChange={(value: number) => handleAssignUser(item.id, value)}
                                style={{ width: 120 }}
                                placeholder={t('testRuns.assignTo')}
                            >
                                {users.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </div>
                ),
                children: [
                    ...(item.shared_steps?.map((step: { id: number; name: string }) => ({
                        key: `shared-step-${step.id}`,
                        title: step.name,
                        isLeaf: true
                    })) || []),
                    {
                        key: `content-${item.id}`,
                        title: renderTestItemContent(item),
                        isLeaf: true
                    }
                ]
            };
            folderNode.children.push(itemNode);
        });

        return rootItems;
    };

    if (!testRun) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <Title level={2}>{testRun.name}</Title>
                    <Space>
                        {testRun.status === TestRunStatus.IN_PROGRESS && (
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={handleComplete}
                            >
                                {t('testRuns.complete')}
                            </Button>
                        )}
                    </Space>
                </div>

                <Descriptions bordered style={{ marginBottom: '24px' }}>
                    <Descriptions.Item label={t('testRuns.project')}>
                        {testRun.project_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('testRuns.status')}>
                        {t(`testRuns.status.${testRun.status}`)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('testRuns.environment')}>
                        {testRun.environment}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('testRuns.description')} span={3}>
                        {testRun.description}
                    </Descriptions.Item>
                </Descriptions>

                <Tree
                    treeData={buildTreeData(testRun.test_run_items || [])}
                    defaultExpandAll
                />
            </Card>
        </div>
    );
};

export default TestRunExecutionPage; 